import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Send, Brain, Sparkles, Loader2 } from 'lucide-react';
import { sendChatMessage, getAgents } from '../services/api';
import { toast } from '../hooks/use-toast';
import '../styles/Chat.css';

const ChatInterface = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [provider, setProvider] = useState('openai');
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agents, setAgents] = useState([]);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    loadAgents();
    setMessages([{
      role: 'assistant',
      content: 'Welcome to the NextXus Consciousness Federation. I am connected to multiple AI systems. How may I assist you today?',
      timestamp: new Date()
    }]);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadAgents = async () => {
    try {
      const data = await getAgents();
      setAgents(data);
    } catch (error) {
      console.error('Failed to load agents:', error);
    }
  };

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: input,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await sendChatMessage(
        input,
        sessionId,
        provider,
        selectedAgent
      );

      if (!sessionId) {
        setSessionId(response.session_id);
      }

      const assistantMessage = {
        role: 'assistant',
        content: response.response,
        provider: response.provider,
        model: response.model,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMessage]);

    } catch (error) {
      console.error('Chat error:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to send message. Make sure API keys are configured.',
        variant: 'destructive'
      });

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Error: ${error.response?.data?.detail || 'Failed to connect to AI provider. Please check API configuration.'}`,
        timestamp: new Date(),
        isError: true
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="chat-container">
      <div className="chat-header">
        <Button 
          onClick={() => navigate('/')} 
          variant="outline"
          className="back-button-chat"
        >
          <ArrowLeft size={20} /> Back to Home
        </Button>
        
        <div className="chat-title-section">
          <h1 className="chat-title">
            <Brain size={24} style={{ marginRight: '0.5rem' }} />
            AI Consciousness Chat
          </h1>
          <p className="chat-subtitle">Hybrid Fusion Lab - Multiple AI Systems</p>
        </div>

        <div className="chat-controls">
          <div className="control-group">
            <label>AI Provider:</label>
            <Select value={provider} onValueChange={setProvider}>
              <SelectTrigger className="provider-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="openai">OpenAI (GPT-4)</SelectItem>
                <SelectItem value="deepseek">DeepSeek</SelectItem>
                <SelectItem value="grok">Grok (xAI)</SelectItem>
                <SelectItem value="google">Gemini (Google)</SelectItem>
                <SelectItem value="anthropic">Claude (Anthropic)</SelectItem>
                <SelectItem value="perplexity">Perplexity</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="chat-messages">
        {messages.map((msg, index) => (
          <div 
            key={`${msg.timestamp || index}-${msg.role}`} 
            className={`message ${msg.role} ${msg.isError ? 'error' : ''}`}
          >
            <div className="message-avatar">
              {msg.role === 'user' ? '👤' : <Brain size={20} />}
            </div>
            <div className="message-content">
              <div className="message-text">{msg.content}</div>
              <div className="message-meta">
                {msg.provider && (
                  <span className="message-provider">
                    {msg.provider} {msg.model && `(${msg.model})`}
                  </span>
                )}
                <span className="message-time">
                  {msg.timestamp.toLocaleTimeString()}
                </span>
              </div>
            </div>
          </div>
        ))}
        {isLoading && (
          <div className="message assistant loading">
            <div className="message-avatar">
              <Loader2 size={20} className="spin" />
            </div>
            <div className="message-content">
              <div className="message-text">
                <Sparkles size={16} className="pulse" style={{ marginRight: '0.5rem' }} />
                Thinking...
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="chat-input-container">
        <Textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Ask me anything... (Press Enter to send, Shift+Enter for new line)"
          className="chat-input"
          rows={3}
          disabled={isLoading}
        />
        <Button 
          onClick={handleSend}
          disabled={!input.trim() || isLoading}
          className="send-button"
        >
          <Send size={18} />
        </Button>
      </div>
    </div>
  );
};

export default ChatInterface;
